import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@api/api';
import { IOrganization, IProject } from '../../slices/auth/authSlice';
import { OrgMembership } from '@app-types/access';

export interface AuthResponse {
  token: string;
  email: string;
  firstName: string;
  lastName: string;
  isActivated?: boolean;
  activated?: boolean;
  projects?: IProject[];
  organizations?: IOrganization[];
  memberships?: OrgMembership[];
}

export interface BasicResponse {
  message?: string;
}

export interface AccountDeletionReadinessResponse {
  canDelete: boolean;
  blockingOrganizations: Array<{
    orgId: string;
    name: string;
    slug: string;
  }>;
}

export type AuthPayloadResponse = Partial<AuthResponse> & BasicResponse;

type FormDataValue = string | number | boolean | null | undefined;
type FormDataCompatible<T> = { [K in keyof T]: FormDataValue };

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
}

export interface ActivationVerifyRequest {
  email: string;
  code: string;
}

export interface ActivationResendRequest {
  email: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface GoogleAuthRequest {
  credential: string;
}

export interface GithubAuthRequest {
  code: string;
}

export interface GitlabAuthRequest {
  code: string;
  redirectUri: string;
}

export interface MicrosoftAuthRequest {
  code: string;
  redirectUri: string;
}

const toFormData = <T extends object>(body: T & FormDataCompatible<T>) => {
  const formData = new FormData();
  (Object.keys(body) as Array<keyof T>).forEach(key => {
    const value = body[key];
    if (value === undefined || value === null || value === '') {
      return;
    }
    formData.append(String(key), String(value));
  });
  return formData;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQuery(),
  endpoints: builder => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: credentials => ({
        url: '/auth/login',
        method: 'POST',
        data: toFormData(credentials),
        suppressErrorToast: true,
      }),
    }),
    register: builder.mutation<AuthPayloadResponse, RegisterRequest>({
      query: body => ({
        url: '/auth/register',
        method: 'POST',
        data: toFormData(body),
        suppressErrorToast: true,
      }),
    }),
    resendActivation: builder.mutation<BasicResponse, ActivationResendRequest>({
      query: body => ({
        url: '/auth/activation/resend',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    verifyActivation: builder.mutation<AuthPayloadResponse, ActivationVerifyRequest>({
      query: body => ({
        url: '/auth/activation/verify',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    requestPasswordReset: builder.mutation<BasicResponse, PasswordResetRequest>({
      query: body => ({
        url: '/auth/password/reset/request',
        method: 'POST',
        data: toFormData(body),
        suppressErrorToast: true,
      }),
    }),
    confirmPasswordReset: builder.mutation<BasicResponse, PasswordResetConfirmRequest>({
      query: body => ({
        url: '/auth/password/reset/confirm',
        method: 'POST',
        data: toFormData(body),
        suppressErrorToast: true,
      }),
    }),
    googleAuth: builder.mutation<AuthResponse, GoogleAuthRequest>({
      query: body => ({
        url: '/auth/google',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    githubAuth: builder.mutation<AuthResponse, GithubAuthRequest>({
      query: body => ({
        url: '/auth/github',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    gitlabAuth: builder.mutation<AuthResponse, GitlabAuthRequest>({
      query: body => ({
        url: '/auth/gitlab',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    microsoftAuth: builder.mutation<AuthResponse, MicrosoftAuthRequest>({
      query: body => ({
        url: '/auth/microsoft',
        method: 'POST',
        data: body,
        suppressErrorToast: true,
      }),
    }),
    getAccountDeletionReadiness: builder.query<AccountDeletionReadinessResponse, void>({
      query: () => ({
        url: '/auth/me/deletion-readiness',
        method: 'GET',
      }),
    }),
    deleteMyAccount: builder.mutation<{ success: boolean }, { confirmation: string }>({
      query: body => ({
        url: '/auth/me',
        method: 'DELETE',
        data: body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useResendActivationMutation,
  useVerifyActivationMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useGoogleAuthMutation,
  useGithubAuthMutation,
  useGitlabAuthMutation,
  useMicrosoftAuthMutation,
  useLazyGetAccountDeletionReadinessQuery,
  useDeleteMyAccountMutation,
} = authApi;
